/**
 * House style for number ↔ unit spacing.
 *
 * Metric/imperial mass-volume SYMBOLS render tight against the number
 * ("350g", "200ml") — no space. Everything else keeps a regular space: word /
 * count units ("2 TL", "3 Zehen", "1 Prise", "4 cloves") and non-unit figures
 * ("412 kcal", "22 min"). This matches the two-user household's preference while
 * leaving the standard spacing on the units where a tight join reads wrong.
 */

const TIGHT_UNITS = new Set(['g', 'kg', 'mg', 'ml', 'l', 'cl', 'dl', 'oz', 'lb']);

/** True for the mass/volume symbols that render tight against the number. */
export function isTightUnit(unit: string | null | undefined): boolean {
  return unit != null && TIGHT_UNITS.has(unit.trim());
}

/**
 * Join a rendered quantity and its unit label with the house separator:
 * no space for tight symbols ("350g"), a regular space otherwise ("2 TL").
 */
export function joinAmount(qty: string, unit: string | null | undefined): string {
  if (!unit) return qty;
  return isTightUnit(unit) ? `${qty}${unit}` : `${qty} ${unit}`;
}

// A number ending (digit or vulgar fraction) + whitespace + a tight unit symbol.
// Two-letter units precede the single-letter `g`/`l` in the alternation so the
// longer unit wins ("ml" not "…l"), and the trailing `\b` keeps it from matching
// a word that merely starts with a unit letter ("2 leaves" is not "2 l"+"eaves").
const VULGAR_FRACTIONS = '¼½¾⅐-⅞';
const TIGHTEN_RE = new RegExp(
  `([0-9${VULGAR_FRACTIONS}])[ \\u00A0\\u202F\\u2009]+(kg|mg|ml|cl|dl|oz|lb|g|l)\\b`,
  'g',
);

/**
 * Tighten already-resolved user-facing text: drop the space between a number and
 * a following tight unit symbol. Used for step instructions, where the quantity
 * and unit come from separate placeholders joined by the literal spaces in the
 * source text (so there's no single join call to route through {@link joinAmount}).
 * Word units and kcal/min are left untouched.
 */
export function tightenUnits(text: string): string {
  return text.replace(TIGHTEN_RE, '$1$2');
}
